import { useDealerDiscounts } from "app/hooks/checkout/useDealerDiscounts";
import { zakelijkProfielCodes } from "features/option-controls/door-width-controls/ZakelijkHelper";
import { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { Product } from "shared/api/middleware-api-client";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";

export const BusinessItemPricing = ({
  className,
  product,
}: {
  className?: string;
  product: Product;
}) => {
  // Dealer discounts, loading
  const {
    businessDoorDiscountsPrice,
    businessDoorDiscount,
    doorOriginalPrice,
    savedAmount,
    isDealerDiscountsLoading,
  } = useDealerDiscounts(product.doorType.price);

  // Door extra options prices
  const doorExtraOptionsPrices = product.extraOptionsPrices;

  // Total configured price: door + extras
  const totalConfiguredPrice = useMemo(() => {
    // Discounted door price
    let total = product.doorType.price ?? 0;

    if (
      zakelijkProfielCodes.includes(product.doorType.code) &&
      product.doorLayout
    ) {
      total += product.doorLayout.price ?? 0;
    }
    // Add configured elements
    total +=
      (product.doorColor.price ?? 0) +
      (product.glassType.price ?? 0) +
      (product.doorHandle.price ?? 0);

    // Add extra options prices
    if (Array.isArray(doorExtraOptionsPrices)) {
      total += doorExtraOptionsPrices.reduce(
        (sum, price) => sum + (price ?? 0),
        0,
      );
    }

    return total;
  }, [doorExtraOptionsPrices, product]);

  // Total price after discount
  const discountedPrice =
    totalConfiguredPrice - totalConfiguredPrice * (businessDoorDiscount / 100);

  // Amount of discount applied
  const discountAmount = (totalConfiguredPrice - discountedPrice).toFixed(2);

  // Calculate the VAT amount based on the total price after discount
  const vatAmount = ((discountedPrice * 21) / 121).toFixed(2);

  return (
    <div className={className}>
      <div className="gg-flex gg-w-full gg-flex-col">
        {/* upper part */}
        <div className="gg-grid gg-w-full gg-grid-cols-2 gg-gap-x-6">
          <p className="gg-text-sm gg-font-bold gg-text-purple-300">
            {!isDealerDiscountsLoading ? (
              getFormattedPrice(product.doorType.price)
            ) : (
              <Skeleton width={98} />
            )}
          </p>
        </div>

        <hr className="gg-my-2.5" />

        <div className="gg-flex gg-w-full gg-flex-col gg-gap-y-1.5 gg-text-xs gg-text-purple-300">
          {/* Subtotaal */}
          <div className="gg-flex gg-w-full gg-justify-between">
            <p>Item subtotaal</p>
            <p className="gg-font-medium">
              {!isDealerDiscountsLoading ? (
                <span> €{totalConfiguredPrice.toFixed(2)}</span>
              ) : (
                <Skeleton width={50} />
              )}
            </p>
          </div>

          {/* Kortingsbedrag */}
          <div className="gg-flex gg-w-full gg-justify-between">
            <p>Kortingsbedrag</p>

            <p className="gg-font-bold gg-text-green-300">
              {!isDealerDiscountsLoading ? (
                <span> -€{discountAmount}</span>
              ) : (
                <Skeleton width={50} />
              )}
            </p>
          </div>

          <hr className="gg-my-1" />

          {/* Incl btw amount */}
          <div className="gg-flex gg-w-full gg-items-center gg-justify-between gg-text-xs">
            <div className="gg-rounded-[100px] gg-bg-grey-600 gg-px-2 gg-py-1 gg-text-center gg-font-medium gg-text-purple-300">
              {!isDealerDiscountsLoading ? (
                <span>
                  <span className="gg-mr-0.5 gg-font-bold gg-italic gg-text-dark-blue">
                    {businessDoorDiscount}%
                  </span>{" "}
                  korting
                </span>
              ) : (
                <Skeleton width={40} />
              )}
            </div>

            <div className="">
              <p className="gg-text-end gg-text-sm gg-font-bold gg-text-dark-blue">
                {!isDealerDiscountsLoading ? (
                  <span> €{discountedPrice.toFixed(2)}</span>
                ) : (
                  <Skeleton width={50} />
                )}
              </p>
              <p className="gg-mt-0.5 gg-text-[11px]">
                {!isDealerDiscountsLoading ? (
                  <span>incl. €{vatAmount} btw</span>
                ) : (
                  <Skeleton width={50} />
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
